import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

export interface ImageProcessOptions {
  maxWidth: number;
  maxFileSize: number;
  quality: number;
}

@Injectable({
  providedIn: 'root',
})
export class ImageCompressor {
  private _document = inject(DOCUMENT);

  private defaultOptions: ImageProcessOptions = {
    maxWidth: 150,
    maxFileSize: 100 * 1024, // 100kb
    quality: 0.9,
  };

  /**
   * Scale image to fixed width while maintaining aspect ratio
   * @param file Input image file
   * @param maxWidth Maximum width (default 150px)
   * @returns Promise resolving to scaled File
   */
  scaleImage(file: File, maxWidth: number = 150): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          // Calculate new dimensions
          const scaleFactor = maxWidth / img.width;
          const newWidth = maxWidth;
          const newHeight = img.height * scaleFactor;

          const canvas = this._document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Unable to get 2D rendering context'));
            return;
          }

          // Set canvas dimensions
          canvas.width = newWidth;
          canvas.height = newHeight;

          // Draw scaled image
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          // Convert to blob
          canvas.toBlob(blob => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }

            const scaledFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            resolve(scaledFile);
          }, file.type);
        };

        img.onerror = error => reject(error);
      };

      reader.onerror = error => reject(error);
    });
  }

  /**
   * Compress image to target file size
   * @param file Input image file
   * @param options Compression options
   * @returns Promise resolving to compressed File
   */
  compressImage(file: File, options: Partial<ImageProcessOptions> = {}): Promise<File> {
    // Merge default and provided options
    const mergedOptions = { ...this.defaultOptions, ...options };

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event: any) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Unable to get 2D rendering context'));
            return;
          }

          // Set canvas dimensions same as original
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw image
          ctx.drawImage(img, 0, 0, img.width, img.height);

          this.compressImageByQuality({
            canvas,
            originalFile: file,
            maxFileSize: mergedOptions.maxFileSize,
            quality: mergedOptions.quality || 0.9,
          })
            .then(resolve)
            .catch(reject);
        };

        img.src = event.target.result;

        img.onerror = error => reject(error);
      };

      reader.onerror = error => reject(error);
    });
  }

  /**
   * Process image with intelligent checks
   * @param file Input image file
   * @param options Processing options
   * @returns Promise resolving to processed File
   */
  async processImage(file: File, options: Partial<ImageProcessOptions> = {}): Promise<File> {
    const mergedOptions = { ...this.defaultOptions, ...options };

    // Check file size
    if (file.size <= mergedOptions.maxFileSize) {
      // If file is already small enough, just scale
      return this.scaleImage(file, mergedOptions.maxWidth);
    }

    // Create image to check dimensions
    const img = await this.createImageFromFile(file);

    // Check image dimensions
    if (img.width <= mergedOptions.maxWidth) {
      // If width is already small, just compress
      return this.compressImage(file, options);
    }

    // Scale first, then compress if needed
    const scaledFile = await this.scaleImage(file, mergedOptions.maxWidth);

    // Compress if still too large
    if (scaledFile.size > mergedOptions.maxFileSize) {
      return this.compressImage(scaledFile, options);
    }

    return scaledFile;
  }

  /**
   * Create Image from File
   * @param file Input file
   * @returns Promise resolving to Image
   */
  private createImageFromFile(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => resolve(img);
        img.onerror = error => reject(error);
      };

      reader.onerror = error => reject(error);
    });
  }

  private compressImageByQuality(options: {
    canvas: HTMLCanvasElement;
    originalFile: File;
    maxFileSize: number;
    quality?: number;
  }): Promise<File> {
    const { canvas, originalFile, maxFileSize, quality = 0.9 } = options;

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          const compressedFile = new File([blob], originalFile.name, {
            type: 'image/jpeg', // Force JPEG for better compression
            lastModified: Date.now(),
          });

          // Size check with quality reduction
          if (compressedFile.size > maxFileSize) {
            if (quality > 0.1) {
              // Recursively try lower quality
              this.compressImageByQuality({ ...options, quality: quality - 0.1 })
                .then(resolve)
                .catch(reject);
            } else {
              reject(new Error('Unable to compress image to target size'));
            }
          } else {
            resolve(compressedFile);
          }
        },
        'image/jpeg', // Force JPEG
        quality
      );
    });
  }
}
